import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

const DEFAULT_OOS_BADGE = "Out of stock";
const MENU_GAP_PX = 4;
const MENU_MAX_HEIGHT_PX = 224; // 14rem
const MENU_ESTIMATE_PER_OPTION_PX = 36;

function resolvePortalTarget(node) {
  if (!node || typeof node.getRootNode !== "function") return null;
  const root = node.getRootNode();
  const host =
    root?.querySelector?.(".weu-custom-cart-drawer") ||
    root?.querySelector?.("#popup-preview-root") ||
    (root instanceof ShadowRoot ? root : null);
  return host || null;
}

/**
 * Compact cart-drawer variant dropdown with an optional tiny OOS badge.
 * Native <option> cannot render HTML badges, so this custom list is required.
 *
 * Menu is portaled to the drawer root and flip-positioned so cart scroll /
 * upsell overflow does not clip the list.
 */
export default function CartDrawerVariantSelect({
  options = [],
  value = "",
  onChange,
  disabled = false,
  style = null,
  ariaLabel = "Variant",
  showOutOfStockBadge = true,
  outOfStockBadgeText = DEFAULT_OOS_BADGE,
  outOfStockBadgeStyle = null,
  className = "",
}) {
  const list = Array.isArray(options) ? options : [];
  const selectedId = String(value || "");
  const selected =
    list.find((option) => String(option.id) === selectedId) || list[0] || null;
  const selectedUnavailable = selected?.available === false;
  const [open, setOpen] = useState(false);
  const [menuPlacement, setMenuPlacement] = useState(null);
  const [portalTarget, setPortalTarget] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const listboxId = useId();
  const badgeText = String(outOfStockBadgeText || "").trim() || DEFAULT_OOS_BADGE;
  const badgeStyle =
    outOfStockBadgeStyle && typeof outOfStockBadgeStyle === "object"
      ? outOfStockBadgeStyle
      : undefined;

  useEffect(() => {
    if (!open) return undefined;
    // Listen inside the shadow root when present. document + event.target is
    // retargeted to the host, so option clicks looked "outside" and closed the
    // menu before the click could select a size (live bug on storefronts).
    const rootNode = rootRef.current?.getRootNode?.();
    const listenTarget =
      rootNode instanceof ShadowRoot ? rootNode : document;

    const eventPath = (event) => {
      if (typeof event.composedPath === "function") {
        try {
          return event.composedPath();
        } catch (_) {
          /* ignore */
        }
      }
      return [];
    };

    const isInsideDropdown = (event) => {
      const path = eventPath(event);
      if (rootRef.current && path.includes(rootRef.current)) return true;
      if (menuRef.current && path.includes(menuRef.current)) return true;
      const target = event.target;
      if (rootRef.current?.contains?.(target)) return true;
      if (menuRef.current?.contains?.(target)) return true;
      return false;
    };

    const onPointerDown = (event) => {
      if (isInsideDropdown(event)) return;
      setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    listenTarget.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      listenTarget.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (!open) {
      setMenuPlacement(null);
      return;
    }
    setPortalTarget(resolvePortalTarget(rootRef.current));
  }, [open]);

  useLayoutEffect(() => {
    if (!open || disabled) return undefined;

    const updatePlacement = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const estimatedHeight = Math.min(
        MENU_MAX_HEIGHT_PX,
        Math.max(list.length, 1) * MENU_ESTIMATE_PER_OPTION_PX + 8
      );
      const measuredHeight = menuRef.current?.offsetHeight || estimatedHeight;
      const spaceBelowViewport = window.innerHeight - rect.bottom - MENU_GAP_PX;
      const spaceAboveViewport = rect.top - MENU_GAP_PX;

      // Prefer panel bounds so menus near cart footer/upsell flip up instead of
      // painting over checkout / gift UI below the strip.
      const panel =
        trigger.closest?.(".weu-custom-cart-drawer__panel") ||
        portalTarget?.querySelector?.(".weu-custom-cart-drawer__panel");
      const panelRect = panel?.getBoundingClientRect?.();
      const spaceBelowPanel = panelRect
        ? panelRect.bottom - rect.bottom - MENU_GAP_PX
        : spaceBelowViewport;
      const spaceAbovePanel = panelRect
        ? rect.top - panelRect.top - MENU_GAP_PX
        : spaceAboveViewport;
      const spaceBelow = Math.min(spaceBelowViewport, spaceBelowPanel);
      const spaceAbove = Math.min(spaceAboveViewport, spaceAbovePanel);
      const openUp =
        spaceAbove >= 72 &&
        (spaceBelow < measuredHeight || spaceBelow < spaceAbove);

      const width = Math.max(rect.width, Math.min(168, window.innerWidth * 0.8));
      let left = rect.left;
      if (left + width > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - width - 8);
      }

      if (openUp) {
        setMenuPlacement({
          openUp: true,
          top: undefined,
          bottom: window.innerHeight - rect.top + MENU_GAP_PX,
          left,
          width,
          maxHeight: Math.min(MENU_MAX_HEIGHT_PX, Math.max(80, spaceAbove)),
        });
      } else {
        setMenuPlacement({
          openUp: false,
          top: rect.bottom + MENU_GAP_PX,
          bottom: undefined,
          left,
          width,
          maxHeight: Math.min(MENU_MAX_HEIGHT_PX, Math.max(80, spaceBelow)),
        });
      }
    };

    updatePlacement();
    const raf = window.requestAnimationFrame(updatePlacement);
    window.addEventListener("resize", updatePlacement);
    window.addEventListener("scroll", updatePlacement, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", updatePlacement);
      window.removeEventListener("scroll", updatePlacement, true);
    };
  }, [open, disabled, list.length, portalTarget]);

  if (list.length < 2) return null;

  const triggerLabel = selected?.label || "Variant";
  const menuStyle = menuPlacement
    ? {
        position: "fixed",
        top: menuPlacement.top,
        bottom: menuPlacement.bottom,
        left: menuPlacement.left,
        width: menuPlacement.width,
        minWidth: menuPlacement.width,
        maxHeight: menuPlacement.maxHeight,
        zIndex: 2147483646,
      }
    : {
        position: "fixed",
        visibility: "hidden",
        pointerEvents: "none",
        zIndex: 2147483646,
      };

  const menu = open && !disabled ? (
    <ul
      ref={menuRef}
      id={listboxId}
      className={`weu-custom-cart-drawer__variant-dd-menu${
        menuPlacement?.openUp
          ? " weu-custom-cart-drawer__variant-dd-menu--up"
          : ""
      }`}
      style={menuStyle}
      role="listbox"
      aria-label={ariaLabel}
    >
      {list.map((option) => {
        const optionId = String(option.id);
        const available = option.available !== false;
        const isSelected = optionId === String(selected?.id || "");
        return (
          <li key={optionId} role="presentation">
            <button
              type="button"
              role="option"
              aria-selected={isSelected}
              aria-disabled={!available}
              disabled={!available}
              className={`weu-custom-cart-drawer__variant-dd-option${
                isSelected
                  ? " weu-custom-cart-drawer__variant-dd-option--selected"
                  : ""
              }${
                !available
                  ? " weu-custom-cart-drawer__variant-dd-option--oos"
                  : ""
              }`}
              onClick={() => {
                if (!available) return;
                onChange?.(optionId);
                setOpen(false);
              }}
            >
              <span className="weu-custom-cart-drawer__variant-dd-option-label">
                {option.label}
              </span>
              {showOutOfStockBadge && !available ? (
                <span
                  className="weu-custom-cart-drawer__variant-oos-badge"
                  style={badgeStyle}
                >
                  {badgeText}
                </span>
              ) : null}
            </button>
          </li>
        );
      })}
    </ul>
  ) : null;

  return (
    <div
      ref={rootRef}
      className={`weu-custom-cart-drawer__variant-select-wrap weu-custom-cart-drawer__variant-dd${
        className ? ` ${className}` : ""
      }${
        open && menuPlacement?.openUp
          ? " weu-custom-cart-drawer__variant-dd--open-up"
          : ""
      }`}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`weu-custom-cart-drawer__variant-select weu-custom-cart-drawer__variant-dd-trigger${
          selectedUnavailable
            ? " weu-custom-cart-drawer__variant-select--oos"
            : ""
        }${open ? " weu-custom-cart-drawer__variant-dd-trigger--open" : ""}`}
        style={style || undefined}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        onClick={() => {
          if (!disabled) setOpen((prev) => !prev);
        }}
      >
        <span className="weu-custom-cart-drawer__variant-dd-trigger-label">
          {triggerLabel}
        </span>
        {showOutOfStockBadge && selectedUnavailable ? (
          <span
            className="weu-custom-cart-drawer__variant-oos-badge"
            style={badgeStyle}
          >
            {badgeText}
          </span>
        ) : null}
      </button>

      {menu
        ? portalTarget
          ? createPortal(menu, portalTarget)
          : menu
        : null}
    </div>
  );
}
